import React, { useState } from "react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Sparkle, FloppyDisk } from "@phosphor-icons/react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import WhyThisMatters from "../WhyThisMatters";
import { track } from "../../lib/analytics";
import { api } from "../../lib/api";
import { toast } from "sonner";

const SECTIONS = [
  { id: "facts", title: "Factual Allegations", field: "drafted_facts", description: "Numbered, chronological factual paragraphs." },
  { id: "claims", title: "Causes of Action", field: "drafted_claims", description: "Counts setting out elements of the claim." },
  { id: "relief", title: "Prayer for Relief", field: "drafted_relief", description: "WHEREFORE clause requesting damages and other relief." },
];

const inputCls = "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

function SafetyRailsBadge({ rails }) {
  if (!rails) return null;
  const redacted = rails.redacted_count || 0;
  const pinned = rails.pinned_count || 0;
  const preserved = (rails.preserved_pins || []).length;
  const hasRedactions = redacted > 0;
  return (
    <div
      className={`mt-3 border p-3 text-xs flex items-start gap-2 ${
        hasRedactions
          ? "border-amber-300 bg-amber-50 text-amber-900"
          : "border-emerald-300 bg-emerald-50 text-emerald-900"
      }`}
      data-testid="ai-safety-rails-badge"
    >
      {hasRedactions ? (
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
      ) : (
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" />
      )}
      <div className="leading-relaxed">
        {hasRedactions ? (
          <>
            <span className="font-medium">Safety rails redacted {redacted} unverified citation{redacted === 1 ? "" : "s"}</span>
            {" "}from this draft. Add the specific statutes, rules, or cases you rely on to your{" "}
            <Link to="../" className="underline underline-offset-4">Citations panel</Link> and re-run the draft to include them.
          </>
        ) : (
          <>
            <span className="font-medium">No unverified citations detected.</span>
            {" "}
            {pinned > 0
              ? `${preserved} of ${pinned} pinned citation${pinned === 1 ? "" : "s"} preserved.`
              : "AI was instructed to omit statutes and case names since no citations are pinned yet."}
          </>
        )}
      </div>
    </div>
  );
}

function SectionEditor({ caseId, section, initialText, save }) {
  const [text, setText] = useState(initialText || "");
  const [generating, setGenerating] = useState(false);
  const [rails, setRails] = useState(null);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/cases/${caseId}/ai-suggest`, { section: section.id });
      track("ai_suggest", { case_id: caseId, section: section.id });
      setText(r.data.text);
      setRails(r.data.safety_rails || null);
      const redacted = r.data.safety_rails?.redacted_count || 0;
      if (redacted > 0) {
        toast.warning(`Drafted ${section.title} · ${redacted} unverified citation${redacted === 1 ? "" : "s"} redacted`);
      } else {
        toast.success(`Drafted ${section.title}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally {
      setGenerating(false);
    }
  };

  const onSave = () => save({ [section.field]: text });

  return (
    <div className="border border-slate-200 p-6" data-testid={`drafting-section-${section.id}`}>
      <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
        <div>
          <h3 className="font-serif-display text-2xl text-slate-900">{section.title}</h3>
          <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={generate} disabled={generating} data-testid={`ai-suggest-${section.id}-btn`} variant="outline" className="rounded-none border-slate-300">
            <Sparkle className={`w-4 h-4 mr-1.5 ${generating ? "animate-pulse" : ""}`} />
            {generating ? "Drafting…" : "AI draft (GPT-5.2)"}
          </Button>
          <Button onClick={onSave} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid={`save-${section.id}-btn`}>
            <FloppyDisk className="w-4 h-4 mr-1.5" /> Save
          </Button>
        </div>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className={`${inputCls} mt-4 min-h-[260px] font-serif-display text-base leading-relaxed`}
        placeholder={`Click "AI draft" to generate this section, or write your own text.`}
        data-testid={`drafting-textarea-${section.id}`}
      />
      <SafetyRailsBadge rails={rails} />
    </div>
  );
}

export default function DraftingTab({ caseData, save }) {
  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="font-serif-display text-3xl text-slate-900">AI-Assisted Drafting</h2>
          <p className="text-slate-600 mt-1 max-w-3xl">
            Use GPT-5.2 to draft each section based on your intake, questionnaire, timeline, and evidence. Always review the text; it is a draft, not legal advice.
          </p>
        </div>
        <span className="label-tiny">Step 5 of 8</span>
      </div>

      <div className="border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700 flex items-start gap-2" data-testid="drafting-safety-notice">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-slate-700" />
        <div className="leading-relaxed">
          <span className="font-medium text-slate-900">Citation safety rails are on.</span>{" "}
          The drafter is instructed to omit any statute, rule, or case name unless you have pinned that
          authority via the Citations panel for this matter. Unverified citations are automatically redacted
          from the generated text.
        </div>
      </div>

      <div className="space-y-6">
        {SECTIONS.map((s) => (
          <SectionEditor
            key={s.id}
            caseId={caseData.case_id}
            section={s}
            initialText={caseData[s.field]}
            save={save}
          />
        ))}
      </div>

      <WhyThisMatters step="drafting" />
    </div>
  );
}
